import { getPlayers } from "@/api/publicRequests/players/getPlayers";
import { ScrollArea } from "@/components/ui/scroll-area";
import { IconButton } from "@/design-system/IconButton/IconButton";
import { Shortcut } from "@/design-system/Kbd/Shortcut";
import {
  PlayerCardWithMetaRating,
  PlayerCardWithMetaRatingSkeleton,
} from "@/design-system/PlayerCard/PlayerCard";
import { SearchBar } from "@/design-system/SearchBar/SearchBar";
import {
  trackSearchOpened,
  trackSearchResultClicked,
} from "@/events/navBarMixpanel";
import useDebounce from "@/hooks/useDebounce";
import { useEscape } from "@/hooks/useKeyboardShortcut";
import { MetaRatingsPlayer } from "@/redux/players/playersSlice";
import { Trans, useTranslation } from "@repo/shared/i18n";
import { Player } from "@repo/shared/types";
import { Check, Plus, Search } from "lucide-react";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";

const MIN_SEARCH_LENGTH = 3;
/** Placeholder cards while a fetch is in flight (~2 rows at the panel width). */
const SKELETON_COUNT = 10;
/** Cards shrink to ~130px so a row of five fits the panel. */
const CARD_SCALE = 0.72;
/** Keycaps for the hint line under the field. */
const SHORTCUT_KEYS = ["Shift", "7"];

/**
 * Desktop-only player search: a round button in the nav bar that opens a
 * centered search panel over a dimmed backdrop. Same backend search as the
 * Players database (`/players?…&search=`), debounced with the shared
 * min-length gate. Opened with the button or Shift+7, closed with Escape, a
 * backdrop click, or picking a player.
 */
export const NavPlayerSearch = () => {
  const { t } = useTranslation(["common", "players"]);
  const [isOpen, setIsOpen] = useState(false);

  // Both entry points funnel through here so the open is reported once, with
  // which of the two the user reached for.
  const open = useCallback((method: "button" | "shortcut") => {
    trackSearchOpened({ method });
    setIsOpen(true);
  }, []);

  /*
    Shift+7 by physical key, so it works whatever the layout prints. "/" is
    accepted too: it is what Shift+7 gives on the Nordic layouts and the
    convention elsewhere. Ignored while the user is typing somewhere else,
    otherwise the panel would eat the character.
  */
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const isShortcut =
        (event.code === "Digit7" && event.shiftKey) || event.key === "/";
      if (!isShortcut) return;
      const target = event.target as HTMLElement | null;
      if (
        target?.isContentEditable ||
        ["INPUT", "TEXTAREA", "SELECT"].includes(target?.tagName)
      ) {
        return;
      }
      event.preventDefault();
      open("shortcut");
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open]);

  return (
    <>
      <IconButton
        shape="round"
        icon={<Search className="size-4 text-neutral-100" />}
        aria-label={t("common:search")}
        onClick={() => open("button")}
      />
      {isOpen && <PlayerSearchPanel onClose={() => setIsOpen(false)} />}
    </>
  );
};

/**
 * Mounted only while open, so the term and results reset on every open and the
 * search effect doesn't run against a closed panel.
 *
 * The nav's search, and also a picker: with `onAdd` each result gets a + that
 * hands the player over (e.g. adding it to My Club) instead of opening it,
 * and the panel stays open so several can be added in one go.
 */
export const PlayerSearchPanel = ({
  onClose,
  onAdd,
  header,
}: {
  onClose: () => void;
  onAdd?: (player: MetaRatingsPlayer) => Promise<unknown> | void;
  /** Above the field, e.g. a title and options for the picker. */
  header?: ReactNode;
}) => {
  const { t } = useTranslation(["common", "players", "nav"]);
  const [searchTerm, setSearchTerm] = useState("");
  const { debouncedSearchTerm, errorMessage } = useDebounce(searchTerm, {
    minLength: MIN_SEARCH_LENGTH,
    contextForErrorMessage: t("players:toolbar.searchForPlayers"),
  });
  const [players, setPlayers] = useState<MetaRatingsPlayer[]>([]);
  /** The term `players` describes; null until a request for one has settled. */
  const [resultsFor, setResultsFor] = useState<string | null>(null);
  /** Players added in this session of the picker, shown with a check. */
  const [added, setAdded] = useState<Set<number>>(new Set());

  /** Adds in flight, so a second click can't add the same player twice. */
  const pending = useRef(new Set<number>());

  const add = async (player: MetaRatingsPlayer) => {
    const id = player.resourceId;
    if (!onAdd || added.has(id) || pending.current.has(id)) return;
    pending.current.add(id);
    try {
      await onAdd(player);
      setAdded((prev) => new Set(prev).add(id));
    } catch {
      // The caller says what went wrong; the player stays addable.
    } finally {
      pending.current.delete(id);
    }
  };

  useEscape(onClose);

  useEffect(() => {
    if (debouncedSearchTerm.length < MIN_SEARCH_LENGTH) {
      setPlayers([]);
      setResultsFor(null);
      return;
    }
    let cancelled = false;
    getPlayers({
      page: 1,
      sort: "meta-rating",
      searchText: debouncedSearchTerm,
    })
      .then(({ players }) => {
        if (!cancelled) setPlayers(players);
      })
      .catch(() => {
        if (!cancelled) setPlayers([]);
      })
      .finally(() => {
        if (!cancelled) setResultsFor(debouncedSearchTerm);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedSearchTerm]);

  /*
    Derived from whether the results describe what is in the field rather than
    tracked in its own state, which is what kept "No players found" off the
    screen between keystrokes: a flag set from the fetch effect only goes up
    after the render that reacted to the new term has already painted, and that
    one frame showed an empty result for a search that had not started. The
    debounce delay counts as searching too — the field is ahead of the request.
  */
  const isSearching =
    searchTerm.length >= MIN_SEARCH_LENGTH && searchTerm !== resultsFor;
  const showEmpty = !isSearching && resultsFor !== null && players.length === 0;
  // Only the first search has nothing to show while it runs. Refining a term
  // keeps the results that are already up, under the spinner, rather than
  // blinking the grid back to skeletons on every keystroke.
  const showSkeletons = isSearching && resultsFor === null;

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-60 flex items-start justify-center p-6 pt-[15vh] bg-black/70 backdrop-blur-[3px] transition-opacity duration-150 ease-out opacity-100 starting:opacity-0"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl rounded-2xl border border-neutral-700 bg-neutral-800 p-4 shadow-[0_30px_80px_rgba(0,0,0,0.6)] transition-all duration-200 ease-out translate-y-0 scale-100 opacity-100 starting:-translate-y-2.5 starting:scale-[0.97] starting:opacity-0"
      >
        {header}
        <SearchBar
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder={t("players:toolbar.searchPlayers")}
          loading={isSearching}
          autoFocus
        />
        {errorMessage && (
          <p className="pt-3 text-sm text-dark-secondary">{errorMessage}</p>
        )}
        {showEmpty && (
          <p className="pt-3 text-sm text-dark-secondary">
            {t("common:noPlayersFound")}
          </p>
        )}
        {(showSkeletons || players.length > 0) && (
          <ScrollArea
            className="mt-3"
            viewportClassName="max-h-[50vh]"
            enableShadows
            shadowDirection="y"
          >
            <div className="grid grid-cols-3 gap-2 min-[640px]:grid-cols-4 min-[768px]:grid-cols-5">
              {showSkeletons
                ? Array.from({ length: SKELETON_COUNT }, (_, i) => (
                    <PlayerCardWithMetaRatingSkeleton
                      key={i}
                      scale={CARD_SCALE}
                      fullWidth
                      hideLabel
                    />
                  ))
                : players.map((player, index) =>
                    onAdd ? (
                      <div key={player.resourceId} className="relative">
                        <PlayerCardWithMetaRating
                          player={player as unknown as Player}
                          onClick={() => add(player)}
                          scale={CARD_SCALE}
                          fullWidth
                          hideLabel
                          hideNewTag
                        />
                        <IconButton
                          shape="round"
                          className={
                            added.has(player.resourceId)
                              ? "absolute top-1.5 right-1.5 bg-upgrade/90 hover:bg-upgrade/90"
                              : "absolute top-1.5 right-1.5 bg-primary hover:bg-primary-hover"
                          }
                          icon={
                            added.has(player.resourceId) ? (
                              <Check className="size-4 text-white" />
                            ) : (
                              <Plus className="size-4 text-white" />
                            )
                          }
                          aria-label={t("common:add")}
                          onClick={() => add(player)}
                        />
                      </div>
                    ) : (
                      <PlayerCardWithMetaRating
                        key={player.resourceId}
                        player={player as unknown as Player}
                        asLink={{ basePath: "/players" }}
                        onClick={() => {
                          trackSearchResultClicked({
                            term_length: searchTerm.length,
                            result_count: players.length,
                            position: index + 1,
                          });
                          onClose();
                        }}
                        scale={CARD_SCALE}
                        fullWidth
                        hideLabel
                        hideNewTag
                      />
                    ),
                  )}
            </div>
          </ScrollArea>
        )}
        {/*
          "on any page" is the whole point of the line: the first person to use
          this read the shortcut as something to press here, inside the panel
          it already opened, where it does nothing.
        */}
        {!onAdd && (
          <p className="pt-3 text-right text-xs text-neutral-500">
            <Trans
              ns="nav"
              i18nKey="searchShortcutHint"
              components={{
                keys: (
                  <Shortcut
                    keys={SHORTCUT_KEYS}
                    className="mr-1 align-middle"
                  />
                ),
              }}
            />
          </p>
        )}
      </div>
    </div>
  );
};
